/*!
 * pem.js - PEM for javascript
 * Copyright (c) 2018, Christopher Jeffrey (MIT License).
 * https://github.com/bcoin-org/bcrypto
 *
 * Resources:
 *   https://tls.mbed.org/kb/cryptography/asn1-key-structures-in-der-and-pem
 *   https://tools.ietf.org/html/rfc1421
 *   https://tools.ietf.org/html/rfc4880
 *   https://github.com/crypto-browserify/EVP_BytesToKey/blob/master/index.js
 *   https://github.com/openssl/openssl/blob/master/include/openssl/pem.h
 *   https://github.com/openssl/openssl/blob/master/crypto/pem/pem_lib.c
 *   https://github.com/openssl/openssl/blob/master/crypto/evp/evp_key.c
 *   https://github.com/openssl/openssl/blob/master/crypto/pem/pem_pkey.c
 */

/* eslint spaced-comment: "off" */
'use strict';

var _regeneratorRuntime = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator");

var _slicedToArray = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/slicedToArray");

var _classCallCheck = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/classCallCheck");

var _createClass = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/createClass");

var _marked = /*#__PURE__*/_regeneratorRuntime.mark(decode);

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var assert = require('bsert');

var base64 = require('../internal/base64');

var _require = require('../internal/util'),
    lines = _require.lines;
/*
 * Constants
 */


var EMPTY = Buffer.alloc(0);
/**
 * PEMBlock
 */

var PEMBlock = /*#__PURE__*/function () {
  function PEMBlock(type, data) {
    _classCallCheck(this, PEMBlock);

    if (type == null) type = 'PRIVACY-ENHANCED MESSAGE';
    if (data == null) data = EMPTY;
    assert(typeof type === 'string');
    assert(Buffer.isBuffer(data));
    this.type = type;
    this.headers = new Map();
    this.data = data;
  }

  _createClass(PEMBlock, [{
    key: "toString",
    value: function toString(armor) {
      return encode(this.type, this.headers, this.data, armor);
    }
  }, {
    key: "fromString",
    value: function fromString(str, armor) {
      var iter = decode(str, armor);
      var it = iter.next();
      if (it.done) throw new Error('No PEM data found.');
      var block = it.value;
      this.type = block.type;
      this.headers = block.headers;
      this.data = block.data;
      return this;
    }
  }, {
    key: "getProcType",
    value: function getProcType() {
      var hdr = this.headers.get('Proc-Type');
      if (!hdr) return null;
      return ProcType.fromString(hdr);
    }
  }, {
    key: "setProcType",
    value: function setProcType(version, state) {
      assert(version != null && state);
      var proc = new ProcType(version, state);
      this.headers.set('Proc-Type', proc.toString());
      return this;
    }
  }, {
    key: "unsetProcType",
    value: function unsetProcType() {
      this.headers.delete('Proc-Type');
      return this;
    }
  }, {
    key: "getDEKInfo",
    value: function getDEKInfo() {
      var hdr = this.headers.get('DEK-Info');
      if (!hdr) return null;
      return DEKInfo.fromString(hdr);
    }
  }, {
    key: "setDEKInfo",
    value: function setDEKInfo(name, iv) {
      assert(name);
      var info = new DEKInfo(name, iv);
      this.headers.set('DEK-Info', info.toString());
      return this;
    }
  }, {
    key: "unsetDEKInfo",
    value: function unsetDEKInfo() {
      this.headers.delete('DEK-Info');
      return this;
    }
  }, {
    key: "isEncrypted",
    value: function isEncrypted() {
      var type;

      try {
        type = this.getProcType();
      } catch (e) {
        return false;
      }

      if (!type) return false;
      return type.version === 4 && type.state === 'ENCRYPTED';
    }
  }], [{
    key: "fromString",
    value: function fromString(str, armor) {
      return new this().fromString(str, armor);
    }
  }]);

  return PEMBlock;
}();
/**
 * ProcType
 */


var ProcType = /*#__PURE__*/function () {
  function ProcType(version, state) {
    _classCallCheck(this, ProcType);

    if (version == null) version = 0;
    if (state == null) state = 'NONE';
    assert(version >>> 0 === version);
    assert(typeof state === 'string');
    this.version = version;
    this.state = state.toUpperCase();
  }

  _createClass(ProcType, [{
    key: "toString",
    value: function toString() {
      return "".concat(this.version, ",").concat(this.state);
    }
  }, {
    key: "fromString",
    value: function fromString(str) {
      assert(typeof str === 'string');
      var parts = str.split(',', 3);
      if (parts.length !== 2) throw new Error('Invalid Proc-Type.');
      this.version = parseU32(parts[0]);
      this.state = parts[1].toUpperCase();
      return this;
    }
  }], [{
    key: "fromString",
    value: function fromString(str) {
      return new this().fromString(str);
    }
  }]);

  return ProcType;
}();
/**
 * DEKInfo
 */


var DEKInfo = /*#__PURE__*/function () {
  function DEKInfo(name, iv) {
    _classCallCheck(this, DEKInfo);

    if (name == null) name = 'AES-128-ECB';
    if (iv == null) iv = EMPTY;
    assert(typeof name === 'string');
    assert(Buffer.isBuffer(iv));
    this.name = name.toUpperCase();
    this.iv = iv;
  }

  _createClass(DEKInfo, [{
    key: "toString",
    value: function toString() {
      var name = this.name;
      if (this.iv.length === 0) return name;
      var iv = this.iv.toString('hex');
      return "".concat(name, ",").concat(iv.toUpperCase());
    }
  }, {
    key: "fromString",
    value: function fromString(str) {
      assert(typeof str === 'string');
      var parts = str.split(',', 3);
      if (parts.length < 1 || parts.length > 2) throw new Error('Invalid DEK-Info.');
      var name = parts[0];
      if (name.length === 0) throw new Error('Invalid DEK-Info name.');
      this.name = name.toUpperCase();
      this.iv = EMPTY;

      if (parts.length > 1) {
        var hex = parts[1];
        var iv = Buffer.from(hex, 'hex');
        if (iv.length !== hex.length >>> 1) throw new Error('Invalid DEK-Info IV.');
        this.iv = iv;
      }

      return this;
    }
  }], [{
    key: "fromString",
    value: function fromString(str) {
      return new this().fromString(str);
    }
  }]);

  return DEKInfo;
}();
/*
 * PEM
 */


function encode(type, headers, data) {
  var armor = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  assert(typeof type === 'string');
  assert(headers instanceof Map);
  assert(Buffer.isBuffer(data));
  assert(typeof armor === 'boolean');
  var str = '';
  str += "-----BEGIN ".concat(type, "-----\n");

  if (headers.size > 0) {
    var _iterator = _createForOfIteratorHelper(headers),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            key = _step$value[0],
            value = _step$value[1];

        str += "".concat(key, ": ").concat(value, "\n");
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    str += '\n';
  }

  var s = base64.encode(data);

  for (var i = 0; i < s.length; i += 64) {
    str += s.substring(i, i + 64) + '\n';
  }

  if (armor) {
    var crc = crc24(data);
    str += "=".concat(base64.encode(crc), "\n");
  }

  str += "-----END ".concat(type, "-----\n");
  return str;
}

function decode(str) {
  var armor,
      chunk,
      block,
      crc,
      _iterator2,
      _step2,
      line,
      index,
      key,
      value,
      preamble,
      type,
      _type,
      _args = arguments;

  return _regeneratorRuntime.wrap(function decode$(_context) {
    while (1) {
      switch (_context.prev = _context.next) {
        case 0:
          armor = _args.length > 1 && _args[1] !== undefined ? _args[1] : false;
          assert(typeof str === 'string');
          assert(typeof armor === 'boolean');
          chunk = '';
          block = null;
          crc = null;
          _iterator2 = _createForOfIteratorHelper(lines(str));
          _context.prev = 7;

          _iterator2.s();

        case 9:
          if ((_step2 = _iterator2.n()).done) {
            _context.next = 60;
            break;
          }

          line = _step2.value;
          index = line.indexOf(':');

          if (!(index !== -1)) {
            _context.next = 19;
            break;
          }

          if (block) {
            _context.next = 15;
            break;
          }

          throw new Error('PEM parse error (misplaced header).');

        case 15:
          key = line.substring(0, index).trim();
          value = line.substring(index + 1).trim();
          block.headers.set(key, value);
          return _context.abrupt("continue", 58);

        case 19:
          if (!(line.length >= 15 && line.substring(0, 5) === '-----')) {
            _context.next = 46;
            break;
          }

          if (!(line.slice(-5) !== '-----')) {
            _context.next = 22;
            break;
          }

          throw new Error('PEM parse error (invalid preamble).');

        case 22:
          preamble = line.slice(5, -5);

          if (!(preamble.substring(0, 6) === 'BEGIN ')) {
            _context.next = 30;
            break;
          }

          if (!block) {
            _context.next = 26;
            break;
          }

          throw new Error('PEM parse error (un-ended block).');

        case 26:
          type = preamble.substring(6).trim();
          block = new PEMBlock();
          block.type = type;
          return _context.abrupt("continue", 58);

        case 30:
          if (!(preamble.substring(0, 4) === 'END ')) {
            _context.next = 45;
            break;
          }

          if (block) {
            _context.next = 33;
            break;
          }

          throw new Error('PEM parse error (unexpected end).');

        case 33:
          _type = preamble.substring(4).trim();

          if (!(block.type !== _type)) {
            _context.next = 36;
            break;
          }

          throw new Error('PEM parse error (type mismatch).');

        case 36:
          block.data = base64.decode(chunk);

          if (!(crc && !crc24(block.data).equals(crc))) {
            _context.next = 39;
            break;
          }

          throw new Error('PEM parse error (invalid armor checksum).');

        case 39:
          _context.next = 41;
          return block;

        case 41:
          chunk = '';
          block = null;
          crc = null;
          return _context.abrupt("continue", 58);

        case 45:
          throw new Error('PEM parse error (unknown preamble).');

        case 46:
          if (block) {
            _context.next = 48;
            break;
          }

          throw new Error('PEM parse error (unexpected data).');

        case 48:
          if (!(line.length === 5 && line.charCodeAt(0) === 0x3d
          /*'='*/
          )) {
            _context.next = 55;
            break;
          }

          if (armor) {
            _context.next = 51;
            break;
          }

          return _context.abrupt("continue", 58);

        case 51:
          if (!crc) {
            _context.next = 53;
            break;
          }

          throw new Error('PEM parse error (unexpected armor checksum).');

        case 53:
          crc = base64.decode(line.substring(1));
          return _context.abrupt("continue", 58);

        case 55:
          if (!(line.length > 96)) {
            _context.next = 57;
            break;
          }

          throw new Error('PEM parse error (line too long).');

        case 57:
          chunk += line.replace(/[\t\v ]/g, '');

        case 58:
          _context.next = 9;
          break;

        case 60:
          _context.next = 65;
          break;

        case 62:
          _context.prev = 62;
          _context.t0 = _context["catch"](7);

          _iterator2.e(_context.t0);

        case 65:
          _context.prev = 65;

          _iterator2.f();

          return _context.finish(65);

        case 68:
          if (!(block || crc)) {
            _context.next = 70;
            break;
          }

          throw new Error('PEM parse error (un-ended block).');

        case 70:
          if (!(chunk.length !== 0)) {
            _context.next = 72;
            break;
          }

          throw new Error('PEM parse error (trailing data).');

        case 72:
        case "end":
          return _context.stop();
      }
    }
  }, _marked, null, [[7, 62, 65, 68]]);
}

function toPEM(data, type, armor) {
  assert(Buffer.isBuffer(data));
  assert(typeof type === 'string');
  var block = new PEMBlock();
  block.type = type;
  block.data = data;
  return block.toString(armor);
}

function fromPEM(str, type, armor) {
  assert(typeof str === 'string');
  assert(typeof type === 'string');
  var block = PEMBlock.fromString(str, armor);
  if (block.type !== type) throw new Error('PEM type mismatch.');
  return block.data;
}
/*
 * Helpers
 */


function crc24(data) {
  assert(Buffer.isBuffer(data));
  var crc = 0xb704ce;

  for (var i = 0; i < data.length; i++) {
    var ch = data[i];
    crc ^= ch << 16;

    for (var j = 0; j < 8; j++) {
      crc <<= 1;
      if (crc & 0x1000000) crc ^= 0x1864cfb;
    }
  }

  crc &= 0xffffff;
  var buf = Buffer.allocUnsafe(3);
  buf[2] = crc;
  crc >>>= 8;
  buf[1] = crc;
  crc >>>= 8;
  buf[0] = crc;
  return buf;
}

function parseU32(str) {
  assert(typeof str === 'string');
  if (str.length < 1 || str.length > 10) throw new Error('Invalid integer.');
  var word = 0;

  for (var i = 0; i < str.length; i++) {
    var ch = str.charCodeAt(i);
    if (ch < 0x30 || ch > 0x39) throw new Error('Invalid integer.');
    word *= 10;
    word += ch - 0x30;
    if (i > 0 && word === 0) throw new Error('Invalid integer.');
    if (word > 0xffffffff) throw new Error('Invalid integer.');
  }

  return word;
}
/*
 * Expose
 */


exports.PEMBlock = PEMBlock;
exports.ProcType = ProcType;
exports.DEKInfo = DEKInfo;
exports.encode = encode;
exports.decode = decode;
exports.toPEM = toPEM;
exports.fromPEM = fromPEM;